import { Dialog, Transition } from "@headlessui/react"
import { Button, cn } from "@/components/common"
import React, { Fragment, useMemo } from "react"

import useToggleState from "@lib/hooks/use-toggle-state"
import ChevronDown from "@modules/common/icons/chevron-down"
import X from "@modules/common/icons/x"

import { getProductPrice } from "@lib/util/get-product-price"
import { convertToLocale } from "@lib/util/money"
import { getLocalizedProductContent } from "@lib/util/product-localization"
import OptionSelect from "./option-select"
import { HttpTypes } from "@medusajs/types"
import { isSimpleProduct } from "@lib/util/product"
import { useTranslations, useLocale } from "next-intl"

import type { RentalInfo } from "@/types/rental"

type MobileActionsProps = {
  product: HttpTypes.StoreProduct
  variant?: HttpTypes.StoreProductVariant
  options: Record<string, string | undefined>
  updateOptions: (title: string, value: string) => void
  inStock?: boolean
  handleAddToCart: () => void
  isAdding?: boolean
  show: boolean
  optionsDisabled: boolean
  rentalInfo?: RentalInfo | null
  onRentClick?: () => void
}

const MobileActions: React.FC<MobileActionsProps> = ({
  product,
  variant,
  options,
  updateOptions,
  inStock,
  handleAddToCart,
  isAdding,
  show,
  optionsDisabled,
  rentalInfo,
  onRentClick,
}) => {
  const t = useTranslations('product')
  const locale = useLocale()
  const { state, open, close } = useToggleState()

  const localizedContent = getLocalizedProductContent(product, locale as any)

  const price = getProductPrice({
    product: product,
    variantId: variant?.id,
  })

  const selectedPrice = useMemo(() => {
    if (!price) {
      return null
    }
    const { variantPrice, cheapestPrice } = price

    return variantPrice || cheapestPrice || null
  }, [price])

  const isSimple = isSimpleProduct(product)

  const showBuyButton = !rentalInfo ||
    !rentalInfo.isRentalAvailable ||
    rentalInfo.availabilityMode === 'sale_only' ||
    rentalInfo.availabilityMode === 'both'

  const showRentButton = rentalInfo?.isRentalAvailable && (
    rentalInfo.availabilityMode === 'rental_only' ||
    rentalInfo.availabilityMode === 'both'
  )

  const handleRentClick = () => {
    onRentClick?.()
  }

  return (
    <>
      <div
        className={cn("lg:hidden inset-x-0 bottom-0 fixed z-50", {
          "pointer-events-none": !show,
        })}
      >
        <Transition
          as={Fragment}
          show={show}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="bg-card flex flex-col gap-y-3 justify-center items-center text-large-regular p-4 h-full w-full border-t border-border"
            data-testid="mobile-actions"
          >
            <div className="flex items-center gap-x-2">
              <span data-testid="mobile-title" className="truncate max-w-[200px]">
                {localizedContent.title}
              </span>
              <span>—</span>
              {showRentButton && rentalInfo?.rentalPrice ? (
                <div className="flex items-end gap-x-1 text-ui-fg-base">
                  <span className="font-semibold">
                    {convertToLocale({
                      amount: rentalInfo.rentalPrice,
                      currency_code: rentalInfo.currencyCode || "CZK",
                      locale: locale,
                    })}
                  </span>
                  <span className="text-small-regular text-muted-foreground">
                    /{t(`rental.durationUnit.${rentalInfo.rentalUnit || 'month'}`, { count: 1 })}
                  </span>
                </div>
              ) : selectedPrice ? (
                <div className="flex items-end gap-x-2 text-ui-fg-base">
                  {selectedPrice.price_type === "sale" && (
                    <p>
                      <span className="line-through text-small-regular">
                        {selectedPrice.original_price}
                      </span>
                    </p>
                  )}
                  <span
                    className={cn({
                      "text-ui-fg-interactive":
                        selectedPrice.price_type === "sale",
                    })}
                  >
                    {selectedPrice.calculated_price}
                  </span>
                </div>
              ) : (
                <div></div>
              )}
            </div>
            <div className={cn("grid w-full gap-x-4", {
              "grid-cols-1": isSimple && (!showRentButton || !showBuyButton),
              "grid-cols-2": !isSimple || (showRentButton && showBuyButton),
              "grid-cols-3": !isSimple && showRentButton && showBuyButton,
            })}>
              {!isSimple && <Button
                onClick={open}
                variant="secondary"
                className="w-full"
                data-testid="mobile-actions-button"
              >
                <div className="flex items-center justify-between w-full">
                  <span>
                    {variant
                      ? Object.values(options).join(" / ")
                      : t('actions.selectOptions')}
                  </span>
                  <ChevronDown />
                </div>
              </Button>}

              {showRentButton && (
                <Button
                  onClick={handleRentClick}
                  disabled={!variant}
                  variant={showBuyButton ? "primary" : "primary"}
                  className="w-full"
                  data-testid="mobile-rent-button"
                >
                  {t('rental.rent')}
                </Button>
              )}

              {showBuyButton && (
                <Button
                  onClick={handleAddToCart}
                  disabled={!inStock || !variant}
                  className="w-full"
                  variant={showRentButton ? "secondary" : "primary"}
                  isLoading={isAdding}
                  data-testid="mobile-cart-button"
                >
                  {!variant
                    ? t('actions.selectVariant')
                    : !inStock
                    ? t('actions.outOfStock')
                    : t('actions.addToCart')}
                </Button>
              )}
            </div>
          </div>
        </Transition>
      </div>
      <Transition appear show={state} as={Fragment}>
        <Dialog as="div" className="relative z-75" onClose={close}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/75 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed bottom-0 inset-x-0">
            <div className="flex min-h-full h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Panel
                  className="w-full h-full transform overflow-hidden text-left flex flex-col gap-y-3"
                  data-testid="mobile-actions-modal"
                >
                  <div className="w-full flex justify-end pr-6">
                    <Button
                      variant="secondary"
                      size="icon"
                      onClick={close}
                      className="bg-card w-12 h-12 rounded-full text-ui-fg-base flex justify-center items-center"
                      data-testid="close-modal-button"
                    >
                      <X />
                    </Button>
                  </div>
                  <div className="bg-card px-6 py-12">
                    {(product.variants?.length ?? 0) > 1 && (
                      <div className="flex flex-col gap-y-6">
                        {(product.options || []).map((option) => {
                          return (
                            <div key={option.id}>
                              <OptionSelect
                                option={option}
                                current={options[option.id]}
                                updateOption={updateOptions}
                                title={option.title ?? ""}
                                disabled={optionsDisabled}
                              />
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export default MobileActions
